<template>
  <div class="w-dashboard">
    <div class="page-header">
      <h2 class="page-title">
        <i class="bi bi-speedometer2"></i>
        {{ $t('label.todayGeneralSummary') }}
      </h2>
    </div>

    <!-- Call Summary Cards Grid -->
    <div class="summary-grid">
      <div
        v-for="(item, index) in summaryBlocks"
        :key="index"
        class="summary-card"
        :class="item.class"
      >
        <div class="card-icon">
          <i :class="item.icon"></i>
        </div>
        <div class="card-content">
          <div class="card-value">{{ item.value }}</div>
          <div class="card-label">{{ item.label }}</div>
        </div>
        <div class="card-decoration"></div>
      </div>
    </div>

    <!-- Customer & ZNS Section -->
    <div class="metrics-row">
      <!-- Customer Cards -->
      <div class="metrics-group">
        <div class="section-header">
          <h3 class="section-title">
            <i class="bi bi-people-fill"></i>
            Thống kê khách hàng
          </h3>
        </div>
        <div class="mini-cards-grid">
          <div
            v-for="(item, index) in customerBlocks"
            :key="'customer-' + index"
            class="summary-card"
            :class="item.class"
          >
            <div class="card-icon">
              <i :class="item.icon"></i>
            </div>
            <div class="card-content">
              <div class="card-value">{{ item.value }}</div>
              <div class="card-label">{{ item.label }}</div>
            </div>
            <div class="card-decoration"></div>
          </div>
        </div>
      </div>

      <!-- ZNS Cards -->
      <div class="metrics-group">
        <div class="section-header">
          <h3 class="section-title">
            <i class="bi bi-chat-square-dots-fill"></i>
            Thống kê ZNS
          </h3>
        </div>
        <div class="mini-cards-grid">
          <div
            v-for="(item, index) in znsBlocks"
            :key="'zns-' + index"
            class="summary-card"
            :class="item.class"
          >
            <div class="card-icon">
              <i :class="item.icon"></i>
            </div>
            <div class="card-content">
              <div class="card-value">{{ item.value }}</div>
              <div class="card-label">{{ item.label }}</div>
            </div>
            <div class="card-decoration"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sales Table Section -->
    <div class="sale-dashboard">
      <div class="section-header">
        <h3 class="section-title">
          <i class="bi bi-table"></i>
          {{ $t('label.todaySummary') }}
        </h3>
      </div>

      <b-table
        striped
        bordered
        hover
        show-empty
        head-variant="dark"
        empty-text="Không có dữ liệu"
        :items="pagedItems"
        :fields="fields"
      >
        <template #cell(stt)="data">
          {{ (currentPage - 1) * perPage + data.index + 1 }}
        </template>

        <template #cell(fullName)="data">
          <div class="font-weight-bold">{{ data.item.fullName }}</div>
          <small class="text-muted">
            {{ salalyList.find(i => i.value === data.item.userType)?.text }}
          </small>
        </template>

        <template #cell(convertRatio)="data">
          <span class="text-info font-weight-bold">
            {{ data.item.convertRatio }}%
          </span>
        </template>

        <template #cell(acceptTask)="data">
          <span class="text-success font-weight-bold">
            {{ data.item.acceptTask }}
          </span>
        </template>

        <template #cell(rejectTask)="data">
          <span class="text-danger font-weight-bold">
            {{ data.item.rejectTask }}
          </span>
        </template>

        <template #cell(noAnswerTask)="data">
          <span class="text-warning font-weight-bold">
            {{ data.item.noAnswerTask }}
          </span>
        </template>

        <template #cell(notCatchTask)="data">
          <span class="text-secondary font-weight-bold">
            {{ data.item.notCatchTask }}
          </span>
        </template>

        <template #cell(totalTasks)="data">
          <span class="text-primary font-weight-bold">
            {{ data.item.totalTasks }}
          </span>
        </template>
      </b-table>

      <!-- Pagination -->
      <div
        v-if="totalRows > 0"
        class="d-flex justify-content-between align-items-center mt-3"
      >
        <div>
          Hiển thị
          <b-form-select
            size="sm"
            v-model="perPage"
            :options="pageOptions"
            class="mx-2"
            style="width: 90px"
          />
          bản ghi
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
        />
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios'

export default {
  data() {
    return {
      dashboardNumbers: {
        convertRatio: 0,
        saleWorking: 0,
        totalTasks: 0,
        acceptTask: 0,
        noAnswerTask: 0,
        rejectTask: 0,
        notCatchTask: 0,
        furtherAdvice: 0,
        ordered: 0,
        // New customer & ZNS fields
        totalCustomers: 0,
        calledCustomers: 0,
        notCalledCustomers: 0,
        znsTargetCustomers: 0,
        znsSuccessCustomers: 0,
        znsFailedCustomers: 0,
        znsNotSentCustomers: 0
      },

      dataTable: [],

      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 20, 50],

      salalyList: [
        { value: 0, text: this.$t('label.salaryKPI') },
        { value: 1, text: this.$t('label.hardSalary') }
      ],

      fields: [
        { key: 'stt', label: 'STT' },
        { key: 'fullName', label: 'Nhân viên' },
        { key: 'totalTasks', label: 'Tổng cuộc gọi' },
        { key: 'convertRatio', label: 'Tỷ lệ (%)' },
        { key: 'acceptTask', label: 'Đồng ý' },
        { key: 'rejectTask', label: 'Từ chối' },
        { key: 'noAnswerTask', label: 'Không nghe máy' },
        { key: 'notCatchTask', label: 'Thuê bao' },
        { key: 'furtherAdvice', label: 'Cần tư vấn thêm' },
        { key: 'ordered', label: 'Số khách đã đặt đơn' }
      ]
    }
  },

  computed: {
    totalRows() {
      return this.dataTable.length
    },

    pagedItems() {
      const start = (this.currentPage - 1) * this.perPage
      return this.dataTable.slice(start, start + this.perPage)
    },

    summaryBlocks() {
      return [
        {
          label: this.$t('label.conversionRate'),
          value: this.dashboardNumbers.convertRatio + '%',
          class: 'conversionRate',
          icon: 'bi bi-graph-up-arrow'
        },
        {
          label: this.$t('label.userActive'),
          value: this.dashboardNumbers.saleWorking,
          class: 'userActive',
          icon: 'bi bi-people-fill'
        },
        {
          label: this.$t('label.totalCall'),
          value: this.dashboardNumbers.totalTasks,
          class: 'totalCall',
          icon: 'bi bi-telephone-fill'
        },
        {
          label: this.$t('label.numberCallDone'),
          value: this.dashboardNumbers.acceptTask,
          class: 'numberCallDone',
          icon: 'bi bi-check-circle-fill'
        },
        {
          label: this.$t('label.numberGuestNoAnswer'),
          value: this.dashboardNumbers.noAnswerTask,
          class: 'numberGuestNoAnswer',
          icon: 'bi bi-telephone-x-fill'
        },
        {
          label: this.$t('label.numberGuestRefuse'),
          value: this.dashboardNumbers.rejectTask,
          class: 'numberGuestRefuse',
          icon: 'bi bi-x-circle-fill'
        },
        {
          label: 'Số lượng thuê bao',
          value: this.dashboardNumbers.notCatchTask,
          class: 'numberGuestNoConnect',
          icon: 'fa fa-ban'
        },
        {
          label: 'Số lượng khách cần tư vấn thêm',
          value: this.dashboardNumbers.furtherAdvice,
          class: 'furtherAdvice',
          icon: 'bi bi-chat-left-dots-fill'
        },
        {
          label: 'Số lượng khách đặt đơn',
          value: this.dashboardNumbers.ordered,
          class: 'ordered',
          icon: 'bi bi-cart-check-fill'
        }
      ]
    },

    customerBlocks() {
      return [
        {
          label: 'Tổng khách hàng',
          value: this.dashboardNumbers.totalCustomers,
          class: 'totalCustomers',
          icon: 'bi bi-person-lines-fill'
        },
        {
          label: 'Đã gọi',
          value: this.dashboardNumbers.calledCustomers,
          class: 'calledCustomers',
          icon: 'bi bi-telephone-outbound-fill'
        },
        {
          label: 'Chưa gọi',
          value: this.dashboardNumbers.notCalledCustomers,
          class: 'notCalledCustomers',
          icon: 'bi bi-telephone-minus-fill'
        }
      ]
    },

    znsBlocks() {
      return [
        {
          label: 'Khách đã đặt đơn',
          value: this.dashboardNumbers.znsTargetCustomers,
          class: 'znsTarget',
          icon: 'bi bi-bullseye'
        },
        {
          label: 'ZNS thành công',
          value: this.dashboardNumbers.znsSuccessCustomers,
          class: 'znsSuccess',
          icon: 'bi bi-check2-circle'
        },
        {
          label: 'ZNS thất bại',
          value: this.dashboardNumbers.znsFailedCustomers,
          class: 'znsFailed',
          icon: 'bi bi-exclamation-circle-fill'
        },
        {
          label: 'Chưa gửi ZNS',
          value: this.dashboardNumbers.znsNotSentCustomers,
          class: 'znsNotSent',
          icon: 'bi bi-send-slash-fill'
        }
      ]
    }
  },

  mounted() {
    this.dashboardCounting()
    this.dashboardSaleSummary()
  },

  methods: {
    async dashboardCounting() {
      const res = await apiClient.get('/api/v1/admin/dashboard/counting')
      if (res && res.data) {
        this.dashboardNumbers = {
          convertRatio: res.data.convertRatio,
          saleWorking: res.data.saleCount,
          totalTasks: res.data.totalTasks,
          acceptTask: res.data.acceptedTasks,
          noAnswerTask: res.data.noAnswerTasks,
          rejectTask: res.data.rejectTasks,
          notCatchTask: res.data.noCatchTasks,
          furtherAdvice: res.data.consultationNeededTasks,
          ordered: res.data.ordered,

          totalCustomers: res.data.totalCustomers,
          calledCustomers: res.data.calledCustomers,
          notCalledCustomers: res.data.notCalledCustomers,
          znsTargetCustomers: res.data.znsTargetCustomers,
          znsSuccessCustomers: res.data.znsSuccessCustomers,
          znsFailedCustomers: res.data.znsFailedCustomers,
          znsNotSentCustomers: res.data.znsNotSentCustomers
        }
      }
    },

    async dashboardSaleSummary() {
      const res = await apiClient.post('/api/v1/admin/dashboard/sale')
      if (res && res.data) {
        this.dataTable = res.data.map(item => ({
          fullName: item.full_name,
          userType: item.role_id,
          acceptTask: item.accept_tasks,
          rejectTask: item.reject_tasks,
          noAnswerTask: item.not_answer_tasks,
          notCatchTask: item.not_catch_tasks,
          furtherAdvice: item.consultation_needed_tasks,
          totalTasks: item.total_tasks,
          convertRatio: item.convert_ratio,
          ordered: item.ordered
        }))
      }
    }
  }
}
</script>

<style scoped lang="stylus">
@import url('https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css')

.w-dashboard
  padding 20px
  background #f8f9fa
  min-height 100vh

/* ===== PAGE HEADER ===== */
.page-header
  margin-bottom 30px

.page-title
  font-size 28px
  font-weight 700
  color #2c3e50
  display flex
  align-items center
  gap 12px
  margin 0
  i
    color #0d6efd
    font-size 32px

/* ===== CALL SUMMARY GRID - 3x3 ===== */
.summary-grid
  display grid
  grid-template-columns repeat(3, 1fr)
  gap 20px
  margin-bottom 30px

/* ===== CUSTOMER & ZNS ROW ===== */
.metrics-row
  display grid
  grid-template-columns 1fr 1fr
  gap 24px
  margin-bottom 40px

.metrics-group
  background white
  border-radius 12px
  padding 20px
  box-shadow 0 2px 8px rgba(0,0,0,0.06)

.mini-cards-grid
  display grid
  grid-template-columns repeat(2, 1fr)
  gap 14px

/* ===== SHARED CARD STYLES ===== */
.summary-card
  position relative
  background white
  border-radius 12px
  padding 24px
  box-shadow 0 2px 8px rgba(0,0,0,0.08)
  display flex
  align-items center
  gap 16px
  overflow hidden
  transition all 0.3s cubic-bezier(0.4, 0, 0.2, 1)
  cursor pointer

  &:hover
    transform translateY(-4px)
    box-shadow 0 8px 24px rgba(0,0,0,0.15)

  .card-decoration
    position absolute
    right -20px
    top -20px
    width 100px
    height 100px
    border-radius 50%
    opacity 0.1
    transition all 0.3s ease

  &:hover .card-decoration
    transform scale(1.2)
    opacity 0.15

  .card-icon
    width 56px
    height 56px
    border-radius 12px
    display flex
    align-items center
    justify-content center
    font-size 24px
    color white
    flex-shrink 0
    transition all 0.3s ease

  &:hover .card-icon
    transform scale(1.1) rotate(5deg)

  .card-content
    flex 1
    min-width 0

  .card-value
    font-size 32px
    font-weight 700
    line-height 1.2
    margin-bottom 4px
    color #2c3e50

  .card-label
    font-size 13px
    color #6c757d
    font-weight 500
    line-height 1.4

  /* ── Call metric colors ── */
  &.conversionRate
    .card-icon
      background linear-gradient(135deg, #20c997 0%, #17a589 100%)
    .card-decoration
      background #20c997

  &.userActive
    .card-icon
      background linear-gradient(135deg, #6f42c1 0%, #5a32a3 100%)
    .card-decoration
      background #6f42c1

  &.totalCall
    .card-icon
      background linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)
    .card-decoration
      background #0d6efd

  &.numberCallDone
    .card-icon
      background linear-gradient(135deg, #20c997 0%, #17a589 100%)
    .card-decoration
      background #20c997

  &.numberGuestNoAnswer
    .card-icon
      background linear-gradient(135deg, #ffc107 0%, #e0a800 100%)
    .card-decoration
      background #ffc107

  &.numberGuestRefuse
    .card-icon
      background linear-gradient(135deg, #dc3545 0%, #c82333 100%)
    .card-decoration
      background #dc3545

  &.numberGuestNoConnect
    .card-icon
      background linear-gradient(135deg, #6c757d 0%, #545b62 100%)
    .card-decoration
      background #6c757d

  &.furtherAdvice
    .card-icon
      background linear-gradient(135deg, #17a2b8 0%, #138496 100%)
    .card-decoration
      background #17a2b8

  &.ordered
    .card-icon
      background linear-gradient(135deg, #fd7e14 0%, #e8590c 100%)
    .card-decoration
      background #fd7e14

  /* ── Customer colors ── */
  &.totalCustomers
    .card-icon
      background linear-gradient(135deg, #3a86ff 0%, #2563eb 100%)
    .card-decoration
      background #3a86ff

  &.calledCustomers
    .card-icon
      background linear-gradient(135deg, #2ec4b6 0%, #17a589 100%)
    .card-decoration
      background #2ec4b6

  &.notCalledCustomers
    .card-icon
      background linear-gradient(135deg, #adb5bd 0%, #868e96 100%)
    .card-decoration
      background #adb5bd

  /* ── ZNS colors ── */
  &.znsTarget
    .card-icon
      background linear-gradient(135deg, #845ef7 0%, #6741d9 100%)
    .card-decoration
      background #845ef7

  &.znsSuccess
    .card-icon
      background linear-gradient(135deg, #40c057 0%, #2f9e44 100%)
    .card-decoration
      background #40c057

  &.znsFailed
    .card-icon
      background linear-gradient(135deg, #fa5252 0%, #e03131 100%)
    .card-decoration
      background #fa5252

  &.znsNotSent
    .card-icon
      background linear-gradient(135deg, #f59f00 0%, #e67700 100%)
    .card-decoration
      background #f59f00

/* ===== TABLE SECTION ===== */
.sale-dashboard
  float left
  margin-top 20px
  width 100%

  span
    line-height 18px

.section-header
  margin-bottom 20px
  padding-bottom 16px
  border-bottom 2px solid #f0f0f0

.section-title
  font-size 20px
  font-weight 600
  color #2c3e50
  display flex
  align-items center
  gap 10px
  margin 0
  i
    color #0d6efd
    font-size 22px

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px)
  .summary-grid
    grid-template-columns repeat(3, 1fr)
    gap 16px

  .summary-card
    padding 20px

  .card-icon
    width 48px
    height 48px
    font-size 20px

  .card-value
    font-size 28px

  .card-label
    font-size 12px

@media (max-width: 992px)
  .summary-grid
    grid-template-columns repeat(2, 1fr)

  .metrics-row
    grid-template-columns 1fr

@media (max-width: 768px)
  .w-dashboard
    padding 16px

  .summary-grid
    grid-template-columns 1fr
    gap 16px

  .summary-card
    padding 20px

  .card-value
    font-size 28px

  .mini-cards-grid
    grid-template-columns 1fr

@media (max-width: 576px)
  .page-title
    font-size 24px

  .section-title
    font-size 18px

  .card-icon
    width 40px
    height 40px
    font-size 18px

  .card-value
    font-size 24px

  .card-label
    font-size 11px
</style>
